import React, { useState, useEffect, useRef } from "react";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL, FIXED_COMPLIANT_LABEL, PARTIALLY_COMPLIANT_LABEL } from "src/constants/constants";
import { Column, Row, Text, Card, Divider, Expander, Icon, Button, Loader } from "@amzn/meridian";
import checkLargeTokens from "@amzn/meridian-tokens/base/icon/check-large";
import closeMediumTokens from "@amzn/meridian-tokens/base/icon/close-medium";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import { ObjectPresenceCheck, Evidence, isOverridden, isFixedNow, isFixedLater } from "src/services/inspectionService";
import ThemedTag from "./ThemedTag";
import OverrideIcon from "./OverrideIcon";
import ObjectPresenceDetailSheet from "./ObjectPresenceDetailSheet";
import CheckpointVerifyFooter from "./CheckpointVerifyFooter";

interface ObjectPresenceCheckpointCardProps {
    questionNumber: string;
    questionText: string;
    allCompliant: boolean;
    isAnalysisPending?: boolean;
    objectChecks: ObjectPresenceCheck[];
    evidence: Evidence[];
    presignedUrlMap: Record<string, string>;
    onOverride?: (check: ObjectPresenceCheck) => void;
    onFixNow?: (check: ObjectPresenceCheck) => void;
    onFixLater?: (check: ObjectPresenceCheck) => void;
    /** Verify / un-verify this checkpoint. Object-presence checkpoints always require it. */
    onVerify?: (verified: boolean) => void | Promise<void>;
    verifiedBy?: string | null;
    verifiedAt?: string | null;
    /** On return from a fix/override action, auto-expand the detailed list if still non-compliant. */
    autoExpand?: boolean;
}

const ObjectPresenceCheckpointCard: React.FC<ObjectPresenceCheckpointCardProps> = ({
    questionNumber,
    questionText,
    allCompliant,
    isAnalysisPending = false,
    objectChecks,
    evidence,
    presignedUrlMap,
    onOverride,
    onFixNow,
    onFixLater,
    onVerify,
    verifiedBy,
    verifiedAt,
    autoExpand = false,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [selectedCheck, setSelectedCheck] = useState<ObjectPresenceCheck | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    const handleOverride = (check: ObjectPresenceCheck) => {
        setSheetOpen(false);
        onOverride?.(check);
    };

    const handleFixNow = (check: ObjectPresenceCheck) => {
        setSheetOpen(false);
        onFixNow?.(check);
    };

    const handleFixLater = (check: ObjectPresenceCheck) => {
        setSheetOpen(false);
        onFixLater?.(check);
    };

    const openSheet = (check: ObjectPresenceCheck) => {
        setSelectedCheck(check);
        setSheetOpen(true);
    };

    const isFixCompliant = allCompliant && objectChecks.some(isFixedNow);

    // At least one check deferred (fix-it-later) and every other check is compliant
    // (on its own or via fix-it-now). A non-compliant check that is not deferred
    // makes the checkpoint Non Compliant instead.
    const isFixLaterPartiallyCompliant =
        objectChecks.some(isFixedLater) &&
        objectChecks.every((c) => c.is_compliant || isFixedLater(c));

    // Mirrors the red "Non Compliant" badge state below: an unresolved
    // non-compliant leaf remains (not all compliant, and not merely deferred).
    const isNonCompliant = !allCompliant && !isFixLaterPartiallyCompliant;

    // On return from a fix/override action (autoExpand), open the inline detailed
    // list once if the checkpoint is still non-compliant, so the inspector doesn't
    // have to tap to see what's left. A useRef one-shot guard keeps the ~3s review
    // poll (which re-renders this card) from re-opening it after a manual collapse.
    const autoExpandedRef = useRef(false);
    useEffect(() => {
        if (!autoExpand) {
            autoExpandedRef.current = false;
            return;
        }
        if (autoExpandedRef.current) return;
        if (isNonCompliant) {
            autoExpandedRef.current = true;
            setShowDetails(true);
        }
    }, [autoExpand, isNonCompliant]);

    // Verification is question-level; the stamp lives on the parent result and is
    // passed in via verifiedBy/verifiedAt.
    const verified = Boolean(verifiedBy);
    // Enabled once settled and either compliant or resolved via fix-it-later.
    const verifyEnabled =
        !isAnalysisPending && (allCompliant || isFixLaterPartiallyCompliant);

    return (
        <Card spacingInset="none">
            <Column spacingInset="400" spacing="300">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h100" color="primary">
                        {questionNumber}
                    </Text>
                    <Row spacing="200" alignmentVertical="center">
                        {isAnalysisPending ? (
                            <Row spacing="200" alignmentVertical="center">
                                <Text type="b100" color="secondary">Analyzing...</Text>
                                <Loader size="small" type="circular" />
                            </Row>
                        ) : isFixCompliant ? (
                            <ThemedTag text={FIXED_COMPLIANT_LABEL} type="fixCompliant" />
                        ) : allCompliant ? (
                            <ThemedTag text={COMPLIANT_LABEL} type="success" />
                        ) : isFixLaterPartiallyCompliant ? (
                            <ThemedTag text={PARTIALLY_COMPLIANT_LABEL} type="fixLater" />
                        ) : (
                            <ThemedTag text={NON_COMPLIANT_LABEL} type="error" />
                        )}
                        {!isAnalysisPending && <ThemedTag text="AI" type="neutral" />}
                    </Row>
                </Row>

                <Text type="b200" color="secondary">
                    {questionText}
                </Text>

                <Divider />

                <Expander
                    title="Detailed compliance status"
                    open={showDetails}
                    onChange={(open: boolean) => setShowDetails(open)}
                    type="inline"
                    spacingInset="none"
                >
                    <Column spacing="none">
                        {objectChecks.map((check, index) => (
                            <Column key={check.object_name} spacingInset="300 none" spacing="400 none">
                                <Row
                                    alignmentHorizontal="justify"
                                    alignmentVertical="center"
                                    spacingInset="200 none"
                                >
                                    <div onClick={() => openSheet(check)}>
                                        <Row alignmentVertical="center" spacing="300" widths={["fit", "fill"]}>
                                            <div
                                                className={`status-circle ${check.is_compliant
                                                        ? (isFixedNow(check)
                                                            ? "status-circle--fix-compliant"
                                                            : "status-circle--compliant")
                                                        : (isFixedLater(check)
                                                            ? "status-circle--fix-later"
                                                            : "status-circle--non-compliant")
                                                    }`}
                                            >
                                                {isFixedLater(check) ? (
                                                    <Icon tokens={checkLargeTokens} color="warning" />
                                                ) : check.is_compliant ? (
                                                    <Icon tokens={checkLargeTokens} color="success" />
                                                ) : (
                                                    <Icon tokens={closeMediumTokens} color="error" />
                                                )}
                                            </div>
                                            <Column spacing="200">
                                                <Text type="b200" color="secondary">
                                                    {check.object_name}
                                                </Text>
                                                {isOverridden(check) && (
                                                    <ThemedTag text="Manually Overridden" type="override" icon={<OverrideIcon />} />
                                                )}
                                            </Column>
                                        </Row>
                                    </div>
                                    <Button type="icon" onClick={() => openSheet(check)}>
                                        <Icon tokens={chevronRightSmallTokens} color="secondary" />
                                    </Button>
                                </Row>
                                {objectChecks.length - 1 !== index && <Divider />}
                            </Column>
                        ))}
                    </Column>
                </Expander>

            </Column>

            {onVerify && !isAnalysisPending && (
                <CheckpointVerifyFooter
                    verified={verified}
                    verifiedBy={verifiedBy}
                    verifiedAt={verifiedAt}
                    enabled={verifyEnabled}
                    onVerify={onVerify}
                />
            )}

            <ObjectPresenceDetailSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                check={selectedCheck}
                onOverride={onOverride ? handleOverride : undefined}
                onFixNow={onFixNow ? handleFixNow : undefined}
                onFixLater={onFixLater ? handleFixLater : undefined}
                evidence={evidence}
                presignedUrlMap={presignedUrlMap}
                questionText={questionText}
                verified={verified}
                verifiedBy={verifiedBy}
                verifiedAt={verifiedAt}
            />
        </Card>
    );
};

export default ObjectPresenceCheckpointCard;
